import { useState } from 'react';

import Image from 'next/image';

import { Box, Button, FieldSet, Heading, Icon, Legend, List, Paragraph, Tag, Text } from '@zillow/constellation-10';
import { IconSparkleFilled } from '@zillow/constellation-icons';

import { ApplicationsLabeledControl } from '../../../../srcLegacy/shared/components/ApplicationsLabeledControl';
import ApplicationSampleModal from '../../modals/ApplicationSampleModal';

import UserInfo from '../../../assets/user-info.svg';

import type { ChangeEvent } from 'react';

type Props = {
  isIntroModal?: boolean;
  isRoomForRent?: boolean;
  radioValue: string;
  onRadioChange: (event: ChangeEvent<Element>) => void;
  serverError?: string;
  disabled?: boolean;
};

const ApplicationsFormField = ({
  isIntroModal = false,
  isRoomForRent = false,
  radioValue,
  onRadioChange,
  serverError,
  disabled = false,
}: Props) => {
  const [displaySampleModal, setDisplaySampleModal] = useState(false);

  return (
    <>
      <Tag tone="info" css={{ marginRight: '200', marginBottom: '400' }}>
        Free
      </Tag>

      <Heading level={2} textStyle="heading-sm">
        {isIntroModal ? 'Receive Zillow applications' : 'Receive Zillow applications for this listing?'}
      </Heading>

      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '600' }}>
        <Box css={{ gridColumnStart: '1', gridColumnEnd: { base: 'span 12', xl: 'span 8' } }}>
          <Paragraph css={{ marginTop: '600' }}>
            {isIntroModal
              ? 'Enable Zillow applications so renters can apply directly from your listing. You’ll automatically receive these benefits:'
              : 'You’ll automatically receive these benefits:'}
          </Paragraph>
          <List.Root>
            <List.Item css={{ paddingBlock: '0' }}>Credit report from identity verified applicants</List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              Background check (includes eviction &amp; criminal record)
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>Income &amp; employment history</List.Item>
            <List.Item css={{ paddingBlock: '0' }}>Residence history &amp; previous landlord contacts</List.Item>
          </List.Root>
          {!isRoomForRent && (
            <Box css={{ marginTop: '600', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Icon
                render={<IconSparkleFilled />}
                size="md"
                css={{ color: 'icon.accent.brand.hero', marginRight: '200' }}
              />
              <Text textStyle="body-sm">
                Your listing gets an additional promotion when you choose to receive Zillow applications
              </Text>
            </Box>
          )}
        </Box>

        <Box
          css={{
            display: 'grid',
            gridColumnStart: { base: '1', xl: '9' },
            gridColumnEnd: { base: 'span 12', xl: 'span 4' },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Image alt="" src={UserInfo} />
            <Button tone="brand" emphasis="filled" onClick={() => setDisplaySampleModal(true)}>
              View sample
            </Button>
          </Box>
        </Box>

        <Box css={{ gridColumnStart: '1', gridColumnEnd: 'span 12' }}>
          {serverError && (
            <Paragraph css={{ color: 'text.feedback.error', marginBottom: '400' }}>{serverError}</Paragraph>
          )}

          <FieldSet required={true} legend={<Legend>Receive Zillow applications for this listing?</Legend>}>
            <Box
              css={{
                display: 'flex',
                paddingLeft: '200',
                flexDirection: 'column',
              }}
            >
              <ApplicationsLabeledControl
                isApplicationsAccepted={radioValue}
                onChange={
                  disabled
                    ? () => {
                        /* disabled */
                      }
                    : onRadioChange
                }
              />
            </Box>
          </FieldSet>
        </Box>
      </Box>

      {displaySampleModal && <ApplicationSampleModal onClose={() => setDisplaySampleModal(false)} />}
    </>
  );
};

export default ApplicationsFormField;
